import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtRp = (v) => `Rp ${Math.round(v || 0).toLocaleString('id-ID')}`;

/**
 * Detect if a plan row triggers the auto PPh 5% cut.
 * Trigger: Company-book project where team's cumulative paid reaches contractValue
 * with this payment (contractValue = team member's contract for the project).
 */
const detectTax = (p) => {
  if ((p.financeBook || 'freelance') !== 'company') return 0;
  const gross = p.amountToPay || 0;
  if (gross <= 0) return 0;
  const after = (p.alreadyPaid || 0) + gross;
  const target = p.contractValue || 0;
  if (target <= 0) return 0;
  const reaches100 = after >= target - 0.5;
  if (!reaches100) return 0;
  return Math.round(target * 0.05);
};

/**
 * Multi-page Payment Planner PDF:
 *  - One "receipt-style" page per project being paid this cycle (short A4)
 *  - Final page: full recap table (as before)
 */
export const generatePaymentPlannerPDF = ({ memberName, plan, dateOverride }) => {
  const doc = new jsPDF({ orientation: 'portrait' });
  const pw = doc.internal.pageSize.getWidth();
  const now = dateOverride ? new Date(dateOverride) : new Date();
  const dateStr = `${now.getDate().toString().padStart(2,'0')}/${(now.getMonth()+1).toString().padStart(2,'0')}/${now.getFullYear()}`;

  const activePlans = (plan || []).filter(p => (p.amountToPay || 0) > 0);

  // ==================== SOLO PAGES ====================
  activePlans.forEach((p, idx) => {
    if (idx > 0) doc.addPage();
    const tax = detectTax(p);
    const gross = p.amountToPay || 0;
    const net = gross - tax;
    let y = 20;

    // Header
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text('KWITANSI PEMBAYARAN', pw / 2, y, { align: 'center' }); y += 6;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Halaman ${idx + 1} dari ${activePlans.length + 1}`, pw / 2, y, { align: 'center' }); y += 8;

    // Book badge
    const isCompany = (p.financeBook || 'freelance') === 'company';
    doc.setFillColor(...(isCompany ? [220, 38, 38] : [37, 99, 235]));
    doc.rect(14, y - 3, 3, 3, 'F');
    doc.setFontSize(9);
    doc.text(isCompany ? 'Company Book' : 'Freelance Book', 19, y);
    y += 6;

    // Info block
    doc.setFontSize(10);
    doc.text(`Nama         : ${memberName}`, 14, y); y += 5;
    doc.text(`Project      : ${p.projectName}`, 14, y); y += 5;
    doc.text(`Tanggal      : ${dateStr}`, 14, y); y += 8;

    // Table: current payment breakdown
    const body = [
      [`${p.myPaymentPercent || 0}% pembayaran`, fmtRp(gross)],
    ];
    if (tax > 0) {
      body.push([`PPh 5% (dari fee ${fmtRp(p.contractValue)})`, `-${fmtRp(tax)}`]);
    }
    body.push([{ content: 'TOTAL DITERIMA', styles: { fontStyle: 'bold' } }, { content: fmtRp(net), styles: { fontStyle: 'bold' } }]);

    autoTable(doc, {
      startY: y,
      head: [['Keterangan', 'Jumlah']],
      body,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [60, 60, 60], textColor: 255 },
      columnStyles: { 1: { halign: 'right', cellWidth: 55 } },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 10;

    // Cumulative info
    doc.setFontSize(9); doc.setTextColor(90);
    doc.text(`Fee Pekerjaan (Team)     : ${fmtRp(p.contractValue)}`, 14, y); y += 5;
    doc.text(`Sudah Dibayar Sebelumnya : ${fmtRp(p.alreadyPaid)} (${p.alreadyPaidPercent || 0}%)`, 14, y); y += 5;
    const totalAfter = (p.alreadyPaid || 0) + gross;
    const totalAfterPct = p.contractValue > 0 ? Math.round(totalAfter / p.contractValue * 100) : 0;
    doc.text(`Total Terbayarkan        : ${fmtRp(totalAfter)} (${totalAfterPct}%)`, 14, y); y += 5;
    doc.setTextColor(0);

    // Signature
    y += 15;
    doc.setFontSize(10);
    doc.text('Diterima oleh,', pw - 60, y); y += 25;
    doc.text(`(${memberName})`, pw - 60, y);
  });

  // ==================== RECAP PAGE ====================
  if (activePlans.length > 0) doc.addPage();

  let y = 15;
  doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('KWITANSI PEMBAYARAN - RECAP', pw / 2, y, { align: 'center' }); y += 8;
  doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  doc.text(`Nama: ${memberName}`, 14, y); y += 5;
  doc.text(`Tanggal: ${dateStr}`, 14, y); y += 8;

  const head = [['No', '', 'Project', 'Kontrak', 'Sudah Dibayar', '%', 'Bayar Kini', 'PPh', 'Total']];
  const body = (plan || []).map((p, i) => {
    const tax = detectTax(p);
    const gross = p.amountToPay || 0;
    const totalAfter = (p.alreadyPaid || 0) + gross;
    const totalAfterPct = p.contractValue > 0 ? Math.round(totalAfter / p.contractValue * 100) : 0;
    return [
      i + 1,
      (p.financeBook || 'freelance') === 'company' ? '__COMPANY__' : '__FREELANCE__',
      p.projectName,
      fmtRp(p.contractValue),
      `${fmtRp(p.alreadyPaid)} (${p.alreadyPaidPercent || 0}%)`,
      `${p.myPaymentPercent || 0}%`,
      fmtRp(gross),
      tax > 0 ? `-${fmtRp(tax)}` : '-',
      `${fmtRp(totalAfter)} (${totalAfterPct}%)`
    ];
  });

  const grandGross = (plan || []).reduce((s, p) => s + (p.amountToPay || 0), 0);
  const grandTax = (plan || []).reduce((s, p) => s + detectTax(p), 0);
  const grandNet = grandGross - grandTax;

  body.push([
    { content: 'TOTAL', colSpan: 6, styles: { fontStyle: 'bold' } },
    { content: fmtRp(grandGross), styles: { fontStyle: 'bold' } },
    { content: grandTax > 0 ? `-${fmtRp(grandTax)}` : '-', styles: { fontStyle: 'bold', textColor: [180, 30, 30] } },
    { content: fmtRp(grandNet), styles: { fontStyle: 'bold' } },
  ]);

  autoTable(doc, {
    startY: y, head, body, theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [60, 60, 60], textColor: 255 },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 5, halign: 'center' },
      3: { halign: 'right' }, 4: { halign: 'right' },
      6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1 && typeof data.cell.raw === 'string') {
        data.cell.text = [''];
      }
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 1 && typeof data.cell.raw === 'string') {
        const marker = data.cell.raw;
        const isCompany = marker === '__COMPANY__';
        const isFreelance = marker === '__FREELANCE__';
        if (!isCompany && !isFreelance) return;
        if (isCompany) doc.setFillColor(220, 38, 38); else doc.setFillColor(37, 99, 235);
        const size = 2.2;
        const cx = data.cell.x + data.cell.width / 2 - size / 2;
        const cy = data.cell.y + data.cell.height / 2 - size / 2;
        doc.rect(cx, cy, size, size, 'F');
      }
    },
    margin: { left: 10, right: 10 },
  });

  const safeName = (memberName || 'member').replace(/\s/g, '_');
  doc.save(`Payment_Planner_${safeName}_${dateStr.replace(/\//g, '-')}.pdf`);
};

/**
 * Convert a stored PostponedPayment document into the `plan` shape expected
 * by generatePaymentPlannerPDF. Handles legacy fields gracefully.
 */
export const plannerToPlan = (planner) => {
  return (planner.projects || []).map(p => ({
    projectName: p.projectName,
    financeBook: p.financeBook || planner.financeBook || 'freelance',
    contractValue: p.contractValue || 0,
    alreadyPaid: p.alreadyPaid || 0,
    alreadyPaidPercent: p.contractValue > 0 ? Math.round((p.alreadyPaid || 0) / p.contractValue * 100) : 0,
    myPaymentPercent: p.paymentPercent || 0,
    amountToPay: p.amount || 0,
  }));
};
